import { useEffect, useCallback, useRef, useState } from 'preact/hooks'
import { useAuth } from '@/auth/useAuth'
import { useWebsterChat } from '@/hooks/useWebsterChat'
import { useStreamingStatus, STATUS_MESSAGES } from '@/hooks/useStreamingStatus'
import * as aiChat from '@/api/aiChat'
import type { AIConversationMessageDto, AIConversationSessionDto } from '@/types/aiChat'
import type { WebsterChatMessage, WebsterAttachment } from '@/types/webster'

interface UseAssetWebsterChatOptions {
    clientCompanyId: string
    workspaceId: string
    /** Existing session id from the entity, if any. Used to restore history. */
    initialSessionId?: string | null
    /** Called the first time the user sends a message and no session id is set.
     *  Returns the new session id. If omitted, sendMessage is a no-op while
     *  the session is missing — useful for flows (LP) where the session is
     *  created server-side as part of asset generation, not by chat. */
    createSession?: () => Promise<string>
}

function buildChatFromSession(messages: AIConversationMessageDto[]): WebsterChatMessage[] {
    const result: WebsterChatMessage[] = []
    messages.forEach((m, i) => {
        const role = (m.Role || '').toLowerCase()
        if (role !== 'user' && role !== 'assistant') return
        // Keep attachment-only turns (empty text + image) — without this an in-chat
        // image upload would replay-drop on reload (ORB-413).
        const attachments = m.Attachments?.map(a => ({
            url: a.Url, fileName: a.FileName, contentType: a.ContentType, note: a.Note,
        }))
        const hasAttachments = !!attachments && attachments.length > 0
        if (!m.Content && !hasAttachments) return
        result.push({
            id: `history-${i}`,
            role: role as 'user' | 'assistant',
            content: m.Content ?? '',
            timestamp: m.Timestamp ? new Date(m.Timestamp) : new Date(),
            createdByName: m.CreatedByName ?? null,
            attachments,
            // Carry the AI-call attribution forward so MessageTokenFooter can
            // render `model · X tokens` under each assistant bubble. Per-message
            // because LP/email rebuilds split into an analyzer reply (Azure
            // OpenAI / Claude analyzer model) and an agent closer (Opus 4.8) —
            // surfacing them separately is honest about which call did what.
            modelId: m.ModelId ?? null,
            outputTokens: m.OutputTokens ?? null,
        })
    })
    return result
}

/**
 * Shared chat lifecycle for any Webster-driven asset (Landing Page, Email
 * Template, Email Copy). Handles session id capture/restore, conversation
 * history replay, and the standard chat-rebuild SignalR event vocabulary.
 *
 * Asset-specific stream events (LP's agent-stream first-generation, thank-you
 * sub-stream) layer on top by reaching into `chat` and `streamingStatus`.
 */
export function useAssetWebsterChat({
    clientCompanyId,
    workspaceId,
    initialSessionId,
    createSession,
}: UseAssetWebsterChatOptions) {
    const chat = useWebsterChat()
    const streamingStatus = useStreamingStatus({ chat })
    const { userProfile } = useAuth()
    // Display name used to attribute the current user's own messages in the
    // chat, so two-user sessions show "Joel: …" / "Kevin: …" on every bubble
    // consistently (peer broadcasts already carry the name from the server).
    // Falls back to user id when first/last name are blank — matches the API's
    // BeginAssetChatTurnAsync fallback so the same identity appears on both
    // sides of a turn.
    const currentUserName = userProfile
        ? `${userProfile.FirstName ?? ''} ${userProfile.LastName ?? ''}`.trim() || userProfile.Id
        : null
    const currentUserId = userProfile?.Id ?? null

    const [sessionId, setSessionId] = useState<string | null>(initialSessionId ?? null)
    const sessionIdRef = useRef<string | null>(initialSessionId ?? null)
    const historyLoadedRef = useRef(false)

    // Turn-lock state for shared asset-chat sessions. Captured from the session
    // GET on hydrate and kept fresh by chatStateChanged SignalR events. When
    // `turnUserId` is set and doesn't match the current user, the chat is
    // locked by someone else — input disables with their name attributed.
    const [turnState, setTurnState] = useState<'Idle' | 'Active'>('Idle')
    const [turnUserId, setTurnUserId] = useState<string | null>(null)
    const [turnUserName, setTurnUserName] = useState<string | null>(null)

    /** Adopt a session id discovered after mount (e.g. from an entity load). */
    const captureSessionId = useCallback((id: string) => {
        if (sessionIdRef.current === id) return
        sessionIdRef.current = id
        setSessionId(id)
    }, [])

    // Replay conversation history when a session id is known. Idempotent: the
    // historyLoadedRef guard prevents re-fetches once we've attempted the load.
    //
    // The bail condition used to be `if (chat.hasMessages) return` — but that
    // broke first-generation attribution on the LP path:
    //   1. API creates session + stamps user message + persists greeting.
    //   2. SB worker runs generation, fires FireFirstGenCompletionMessageAsync
    //      which broadcasts a `conversationalResponse` SignalR event.
    //   3. Dashboard's onConversationalResponse adds Webster's reply to chat
    //      BEFORE the LP doc reload surfaces the new ConversationSessionId
    //      and triggers this effect.
    //   4. By the time we run, chat.hasMessages is already true — we bailed,
    //      and the persisted first user message (carrying its real
    //      CreatedByName) never got hydrated. Attribution only appeared on
    //      the second user message onward; remounting the chat would fix it.
    // New gate: bail only when the local chat already has a USER message —
    // that's the "active session resumption" / "I'm already typing here" case
    // where server has nothing newer than what's on screen. SignalR-only
    // assistant messages don't count and still trigger the hydrate (history
    // is a superset of them since FireFirstGenCompletionMessageAsync also
    // persists via AppendAssistantMessageAsync).
    useEffect(() => {
        if (!sessionId) return
        if (historyLoadedRef.current) return
        if (chat.messages.some(m => m.role === 'user')) {
            historyLoadedRef.current = true
            return
        }
        let cancelled = false
        historyLoadedRef.current = true
        aiChat.getConversation(clientCompanyId, workspaceId, sessionId)
            .then(session => {
                if (cancelled || !session) return
                const history = buildChatFromSession(session.Messages)
                if (history.length > 0) chat.initializeFromHistory(history)

                // Hydrate the turn-lock state so a user landing on a page mid-turn
                // sees the input locked immediately with the holder's name —
                // no flicker waiting for the next SignalR event.
                setTurnState((session.TurnState as 'Idle' | 'Active') ?? 'Idle')
                setTurnUserId(session.TurnUserId ?? null)
                setTurnUserName(session.TurnUserName ?? null)
            })
            .catch(err => console.warn('Failed to load chat history:', err))
        return () => { cancelled = true }
    }, [sessionId, clientCompanyId, workspaceId])

    /** Send a chat message. Lazily creates a session via `createSession` if
     *  none is set and a factory was provided; otherwise no-ops silently.
     *  Returns the conversation session DTO (callers can read Messages off it)
     *  or null if not sent. */
    const sendMessage = useCallback(async (message: string, attachments?: WebsterAttachment[]): Promise<AIConversationSessionDto | null> => {
        const hasAttachments = !!attachments && attachments.length > 0
        // The image can stand as the message — an attachment-only turn ("add this image")
        // is valid on its own. Trim so a whitespace-only string doesn't masquerade as text
        // (the bubble then renders the image chip alone, no phantom blank text).
        const trimmedMessage = message.trim()
        if (!trimmedMessage && !hasAttachments) return null
        if (!sessionIdRef.current && !createSession) {
            // Caller's UI should gate input until a session exists in this mode.
            return null
        }
        // Attribute the local bubble with the current user's name so attribution
        // is consistent across self + peer messages once spectators join the
        // session. The dedupe path in onUserMessageAppended uses createdById
        // (not name) so this never causes a double-render of our own send.
        chat.addUserMessage(trimmedMessage, { createdByName: currentUserName, attachments })
        streamingStatus.beginStreaming()
        try {
            let sid = sessionIdRef.current
            if (!sid && createSession) {
                sid = await createSession()
                sessionIdRef.current = sid
                setSessionId(sid)
            }
            if (!sid) return null
            // Map the composer's lowercase WebsterAttachment shape to the PascalCase wire DTO.
            const wireAttachments = hasAttachments
                ? attachments!.map(a => ({ Url: a.url, FileName: a.fileName, ContentType: a.contentType, Note: a.note }))
                : undefined
            return await aiChat.sendMessage(clientCompanyId, workspaceId, sid, trimmedMessage, undefined, wireAttachments)
        } catch (err) {
            // Surface 409 (TurnAlreadyActive) and 410 (ConversationExpired) as
            // chat-level system messages instead of the generic "something went
            // wrong" toast — they're expected user states, not service errors.
            if (err instanceof aiChat.TurnAlreadyActiveError) {
                chat.removeMessage(streamingStatus.streamMsgId.current)
                chat.setGenerating(false)
                console.info('Send blocked — turn already active:', err.turnUserName)
                return null
            }
            if (err instanceof aiChat.ConversationExpiredError) {
                // Update + finalize in place — the streaming bubble already exists
                // from beginStreaming() above, so removing it first would just force
                // updateStreamingMessage's upsert path to recreate it (wasteful and
                // makes the intent of "swap the in-flight message for the expiration
                // notice" harder to read).
                chat.updateStreamingMessage(streamingStatus.streamMsgId.current, {
                    content: 'This conversation expired. Refresh to start a new one.',
                    streamStatus: undefined,
                })
                chat.finalizeStreamingMessage(streamingStatus.streamMsgId.current)
                chat.setGenerating(false)
                return null
            }
            console.error('useAssetWebsterChat.sendMessage failed:', err)
            chat.updateStreamingMessage(streamingStatus.streamMsgId.current, {
                content: 'Sorry, something went wrong. Please try again.',
                streamStatus: undefined,
            })
            chat.finalizeStreamingMessage(streamingStatus.streamMsgId.current)
            chat.setGenerating(false)
            return null
        }
    }, [clientCompanyId, workspaceId, chat, streamingStatus, createSession])

    // Server-initiated generation (email auto-gen on asset create) has no sendMessage to
    // open the working indicator, so the elapsed clock would otherwise first appear on the
    // first SignalR event — which lands seconds after generation actually started, making
    // the on-screen timer look like it resets when the streaming line takes over. Open the
    // streaming bubble at generation start so the clock is continuous AND `generating`
    // reflects the real lifecycle (true now → false on rebuilt), independent of any
    // post-generation animation (e.g. the email rocket) the caller runs.
    const beginAutoGeneration = useCallback(() => {
        streamingStatus.beginStreaming()
    }, [streamingStatus])

    // ---- Standard chat-rebuild SignalR handlers --------------------------
    // Server fires `conversationalResponse` for every classification. On the proceed
    // branch it carries `WillRebuild: true` — we immediately finalize the personalized
    // response bubble AND open a fresh working-indicator bubble below, so the user
    // sees continuous activity instead of dead time while the service bus message is
    // in flight before the queue worker fires its events. Subsequent status events
    // (thinking/content/tool_use/rebuilding) flow into the working bubble via the
    // shared `streamMsgId`. The bubble is removed on `rebuilt` (email asset, fires
    // explicitly) or on `result` (LP, the agent-stream pipeline's completion event).
    //
    // `resultHandledRef` is set by `onConversationalResponse`, reset on
    // `beginStreaming`. It's the signal that "the primary response is in the chat —
    // status events belong on a separate working bubble."

    const onAnalyzing = useCallback(() => {
        // Pass the actual semantic state ('analyzing') as the type — the streaming
        // bubble's elapsed-time label reads streamStatus.type and maps it to a human
        // label, so passing 'thinking' here would render "(15s · still thinking)"
        // when the system is actually analyzing the user's edit intent.
        streamingStatus.updateStatus('analyzing', STATUS_MESSAGES.analyzing)
    }, [streamingStatus])

    const onRebuilding = useCallback(() => {
        // If we already opened a working bubble in onConversationalResponse, this
        // event just updates its status text. Otherwise (older API w/o WillRebuild,
        // or first-gen edge cases) fall back to opening here.
        if (streamingStatus.resultHandledRef.current && !streamingStatus.streamMsgId.current.startsWith('rebuild-')) {
            streamingStatus.beginNewBubble('rebuilding', 'rebuild')
            return
        }
        // Same reasoning as onAnalyzing: pass the semantic state, not 'thinking'.
        streamingStatus.updateStatus('rebuilding', STATUS_MESSAGES.rebuilding)
    }, [streamingStatus])

    const onRebuilt = useCallback(() => {
        // Lifecycle-only since the ORB-294 followup. The visible Webster reply
        // arrives via assistantMessageAppended (Opus's inline <message> tag
        // extracted by the server's GenerateViaAgentAsync, persisted + broadcast
        // in one event). All this handler does is tear down the working
        // indicator and stop the streaming spinner.
        //
        // Two indicator shapes can be live when rebuilt arrives:
        //   1. `rebuild-` prefix — opened by onConversationalResponse for a
        //      chat-driven edit (WillRebuild=true case).
        //   2. The initial-gen streaming bubble — no conversationalResponse
        //      fired this turn, so resultHandledRef is still false.
        // Either way, drop the indicator so assistantMessageAppended's bubble
        // stands alone. A third shape — resultHandledRef=true AND the active
        // bubble is NOT a `rebuild-` indicator — would mean a finalized AI
        // bubble has the active id; leaving it alone is correct.
        const activeId = streamingStatus.streamMsgId.current
        const isRebuildIndicator = activeId.startsWith('rebuild-')
        const isInitialGenIndicator = !streamingStatus.resultHandledRef.current
        if (isRebuildIndicator || isInitialGenIndicator) {
            chat.removeMessage(activeId)
        }
        chat.setGenerating(false)
    }, [chat, streamingStatus])

    const onConversationalResponse = useCallback((
        content: string,
        willRebuild: boolean,
        modelId: string | null,
        outputTokens: number | null,
        rebuildMode?: 'edit' | 'rebuild',
    ) => {
        // Defense against a server bug where the event arrives with empty Content.
        // Without this guard the streaming bubble would finalize with empty content
        // (AssistantMessage then renders nothing — a silent drop) AND we'd still
        // open the rebuild bubble if willRebuild=true, which would orphan if no
        // rebuild followed. Bail out cleanly so the streaming bubble keeps cycling
        // status until something definitive arrives, and don't toggle generating.
        if (!content) {
            console.warn('useAssetWebsterChat: ignoring conversationalResponse with empty content')
            return
        }

        streamingStatus.resultHandledRef.current = true
        // Stamp the analyzer's model+tokens on the finalized bubble so
        // MessageTokenFooter renders attribution immediately — without these
        // updates the just-completed bubble would only get a footer after a
        // chat reload (the persisted message carries them but the live
        // upserted message wouldn't).
        chat.updateStreamingMessage(streamingStatus.streamMsgId.current, {
            content,
            streamStatus: undefined,
            modelId,
            outputTokens,
        })
        chat.finalizeStreamingMessage(streamingStatus.streamMsgId.current)

        if (willRebuild) {
            // Immediately open the working-indicator bubble below the response. A
            // targeted edit shows a distinct "Applying changes…" voice; a full
            // rebuild shows rebuilding-pool status until the queue worker picks up
            // the message and starts firing thinking/content/tool_use events.
            streamingStatus.beginNewBubble('rebuilding', 'rebuild', rebuildMode === 'edit')
        } else {
            chat.setGenerating(false)
        }
    }, [chat, streamingStatus])

    /**
     * Server-driven append of a finalized Webster bubble outside the
     * sendMessage round-trip. Currently fired by the rebuild flow's
     * AgentCostRecorder closer ("Page updated." with the agent's model).
     * Stamps model+tokens so MessageTokenFooter renders without a chat reload.
     */
    const onAssistantMessageAppended = useCallback((
        content: string,
        modelId: string | null,
        outputTokens: number | null,
    ) => {
        if (!content) {
            console.warn('useAssetWebsterChat: ignoring assistantMessageAppended with empty content')
            return
        }
        chat.addAssistantMessage(content, { modelId, outputTokens })
    }, [chat])

    const onError = useCallback((message: string) => {
        chat.updateStreamingMessage(streamingStatus.streamMsgId.current, {
            content: `Sorry, something went wrong. ${message}`.trim(),
            streamStatus: undefined,
        })
        chat.finalizeStreamingMessage(streamingStatus.streamMsgId.current)
        chat.setGenerating(false)
    }, [chat, streamingStatus])

    // The server rejected the rebuild and kept the prior draft (e.g. an edit that would
    // have broken a protected template slot). This is NOT an error — surface the server's
    // friendly explanation as a normal Webster bubble and stop the working indicator.
    // Mirrors onError's bubble mechanics but without the "Sorry, something went wrong"
    // framing. The active bubble here is whatever streamMsgId points at — the `rebuild-`
    // working indicator opened after conversationalResponse, or the original streaming
    // bubble if the rebuild ran without one. The caller intentionally does NOT refetch
    // the asset: the draft is unchanged.
    const onRebuildRejected = useCallback((content: string) => {
        const text = content?.trim()
            || "I couldn't make that change safely, so I've kept your previous version. Try describing the change a little differently."
        chat.updateStreamingMessage(streamingStatus.streamMsgId.current, {
            content: text,
            streamStatus: undefined,
        })
        chat.finalizeStreamingMessage(streamingStatus.streamMsgId.current)
        chat.setGenerating(false)
    }, [chat, streamingStatus])

    /**
     * SignalR-driven turn lock state change. Wire this to the LP / Email asset
     * SignalR hook's `onChatStateChanged` callback. The caller's input component
     * reads `turnUserName` against the current user id to render
     * "Joel is asking Webster…" and disable input while another user holds the turn.
     */
    const onChatStateChanged = useCallback((
        state: 'Idle' | 'Active',
        nextTurnUserId: string | null,
        nextTurnUserName: string | null,
    ) => {
        setTurnState(state)
        setTurnUserId(state === 'Active' ? nextTurnUserId : null)
        setTurnUserName(state === 'Active' ? nextTurnUserName : null)
    }, [])

    /**
     * SignalR-driven user message broadcast from another client in the asset-chat
     * group. The server broadcasts to *every* connection in the group including
     * the sender's, so we suppress the echo when the broadcast's createdById
     * matches the current user (we already added the local bubble in sendMessage).
     * Anything else is a peer message — append it so spectators see "Joel:
     * remove the logo" land in real time.
     */
    const onUserMessageAppended = useCallback((
        content: string,
        createdById: string | null,
        createdByName: string | null,
    ) => {
        if (!content) return
        if (createdById && currentUserId && createdById === currentUserId) {
            // Our own echo — local bubble already rendered in sendMessage.
            return
        }
        chat.addUserMessage(content, { createdByName })
        // No beginStreaming: spectators shouldn't show their own working
        // indicator. They see the chat state change to Active via
        // onChatStateChanged separately, and Webster's reply streams into a
        // working bubble via the existing analyzing/conversationalResponse/result
        // event flow.
    }, [chat, currentUserId])

    return {
        // State
        chat,
        streamingStatus,
        messages: chat.messages,
        generating: chat.generating,
        hasMessages: chat.hasMessages,
        sessionId,
        // Turn lock state — shared across all users editing this asset.
        // Consumer is expected to compare `turnUserId` against current user id
        // to compute "locked by someone else" and drive the input placeholder.
        turnState,
        turnUserId,
        turnUserName,
        // Actions
        captureSessionId,
        sendMessage,
        beginAutoGeneration,
        // Standard SignalR handlers
        onAnalyzing,
        onRebuilding,
        onRebuilt,
        onRebuildRejected,
        onConversationalResponse,
        onAssistantMessageAppended,
        onError,
        // Multi-user turn-lock handlers — wire to the asset's SignalR hook.
        onChatStateChanged,
        onUserMessageAppended,
    }
}
